import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { LayoutTemplate, Sparkles, Calendar, ListTree, Flag, Workflow, ArrowUpRight, Store, Globe, Lock } from "lucide-react";
import { toast } from "sonner";

const METHODOLOGY_BADGE = {
  agile: "bg-blue-100 text-blue-700",
  waterfall: "bg-amber-100 text-amber-700",
  hybrid: "bg-violet-100 text-violet-700",
};

const TYPE_ICON = {
  phase: Workflow,
  milestone: Flag,
  task: ListTree,
};

const Templates = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [deals, setDeals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [openInstantiate, setOpenInstantiate] = useState(false);
  const [openPublish, setOpenPublish] = useState(false);
  const [publishForm, setPublishForm] = useState({ price: 0, currency: "GBP" });
  const [form, setForm] = useState({ project_name: "", start_date: new Date().toISOString().slice(0, 10), deal_id: "" });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [tplRes, dealRes] = await Promise.all([
      api.get("/templates"),
      api.get("/deals").catch(() => ({ data: [] })),
    ]);
    setTemplates(tplRes.data);
    setDeals(dealRes.data);
    if (!selected && tplRes.data[0]) setSelected(tplRes.data[0]);
    if (selected) {
      const fresh = tplRes.data.find((t) => t.template_id === selected.template_id);
      if (fresh) setSelected(fresh);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOwnedByMe = selected && !selected.is_system && selected.owner_id === user?.user_id;

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/templates/${selected.template_id}/publish`, {
        price: Number(publishForm.price) || 0,
        currency: publishForm.currency,
      });
      toast.success(Number(publishForm.price) > 0 ? "Template listed on the marketplace" : "Template published as free");
      setOpenPublish(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to publish");
    } finally {
      setBusy(false);
    }
  };

  const unpublish = async () => {
    if (!window.confirm("Unpublish this template? It will be removed from the marketplace.")) return;
    try {
      await api.post(`/templates/${selected.template_id}/unpublish`);
      toast.success("Unpublished");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unpublish");
    }
  };

  const openDialog = () => {
    if (!selected) return;
    setForm({
      project_name: "",
      start_date: new Date().toISOString().slice(0, 10),
      deal_id: "",
    });
    setOpenInstantiate(true);
  };

  const instantiate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const payload = {
        start_date: form.start_date,
        deal_id: form.deal_id || null,
      };
      // Only send project_name if user typed one — let backend derive from deal or template
      if (form.project_name.trim()) payload.project_name = form.project_name.trim();
      const { data } = await api.post(`/templates/${selected.template_id}/instantiate`, payload);
      toast.success(`Project created (${data.items_created} items)`);
      setOpenInstantiate(false);
      navigate(`/workspace/${data.project_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to instantiate template");
    } finally {
      setBusy(false);
    }
  };

  // Build hierarchy: top-level items (no parent) + their children
  const groupedItems = (() => {
    if (!selected) return [];
    const items = selected.items || [];
    const topLevel = items.filter((i) => !i.parent_template_item_id);
    return topLevel.map((parent) => ({
      ...parent,
      children: items.filter((i) => i.parent_template_item_id === parent.template_item_id),
    }));
  })();

  return (
    <div>
      <PageHeader
        eyebrow="PMO Library"
        title="Template Library"
        description="Start projects from battle-tested Work Breakdown Structures. Phases, milestones and tasks with relative scheduling."
        actions={
          <div className="flex gap-2">
            <Link to="/marketplace">
              <Button variant="outline" className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent" data-testid="open-marketplace-btn">
                <Store className="w-4 h-4 mr-1" /> Marketplace
              </Button>
            </Link>
            {isOwnedByMe && !selected?.is_public && (
              <Button
                onClick={() => { setPublishForm({ price: 0, currency: "GBP" }); setOpenPublish(true); }}
                variant="outline"
                className="rounded-none border-gray-300 text-gray-600 hover:text-gray-900 bg-transparent"
                data-testid="publish-template-btn"
              >
                <Globe className="w-4 h-4 mr-1" /> Publish
              </Button>
            )}
            {isOwnedByMe && selected?.is_public && (
              <Button
                onClick={unpublish}
                variant="outline"
                className="rounded-none border-[#374151] text-[#6B7280] hover:text-white bg-transparent"
                data-testid="unpublish-template-btn"
              >
                <Lock className="w-4 h-4 mr-1" /> Unpublish
              </Button>
            )}
            <Button onClick={openDialog} disabled={!selected} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="instantiate-template-btn">
              <Sparkles className="w-4 h-4 mr-1" /> Create project from template
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-12 min-h-[70vh]">
        <aside className="col-span-12 lg:col-span-4 border-r border-[#1F2937]">
          <div className="px-6 py-3 border-b border-[#1F2937] text-[11px] uppercase tracking-[0.25em] text-gray-500 flex items-center justify-between">
            <span>All templates</span>
            <span className="font-mono text-gray-400">{templates.length}</span>
          </div>
          {templates.length === 0 && (
            <div className="p-8 text-sm text-gray-500">No templates yet.</div>
          )}
          {templates.map((t) => (
            <button
              key={t.template_id}
              onClick={() => setSelected(t)}
              className={`w-full text-left px-6 py-5 border-b border-[#1F2937] hover:bg-[#030712] ${selected?.template_id === t.template_id ? "bg-[#030712] border-l-2 border-l-[#002FA7]" : "border-l-2 border-l-transparent"}`}
              data-testid={`template-item-${t.template_id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-display font-bold text-base tracking-tight">{t.name}</div>
                {t.is_system && (
                  <span className="text-[9px] uppercase tracking-[0.2em] text-gray-400 border border-[#1F2937] px-1.5 py-0.5">System</span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-1 line-clamp-2">{t.description}</div>
              <div className="flex items-center gap-2 mt-3">
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 ${METHODOLOGY_BADGE[t.methodology] || "bg-[#111827] text-slate-300"}`}>{t.methodology}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{t.items?.length || 0} items</span>
              </div>
            </button>
          ))}
        </aside>

        <section className="col-span-12 lg:col-span-8 p-8 bg-[#030712]">
          {!selected ? (
            <div className="text-sm text-gray-500">Select a template to preview its work breakdown.</div>
          ) : (
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#002FA7] grid place-items-center">
                    <LayoutTemplate className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-[#002FA7]">{selected.methodology} · {selected.items?.length || 0} items</div>
                    <h2 className="font-display font-black text-3xl tracking-tighter">{selected.name}</h2>
                  </div>
                </div>
                <p className="text-slate-300 mt-3 max-w-2xl">{selected.description}</p>
              </div>

              <div className="bg-[#0D1117] border border-[#1F2937]">
                <div className="px-5 py-3 border-b border-[#1F2937] text-[11px] uppercase tracking-[0.25em] text-gray-500 flex items-center justify-between">
                  <span>Work Breakdown Structure</span>
                  <span className="text-gray-400 font-mono">days relative to start</span>
                </div>
                <div className="divide-y divide-[#1F2937]">
                  {groupedItems.map((parent) => {
                    const ParentIcon = TYPE_ICON[parent.item_type] || ListTree;
                    return (
                      <div key={parent.template_item_id} data-testid={`wbs-phase-${parent.template_item_id}`}>
                        <div className="px-5 py-3 bg-[#030712] flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ParentIcon className="w-4 h-4 text-[#002FA7]" strokeWidth={1.7} />
                            <span className="font-display font-bold text-sm tracking-tight">{parent.title}</span>
                            <span className="text-[9px] uppercase tracking-[0.2em] text-gray-500 border border-[#1F2937] px-1.5 py-0.5">{parent.item_type}</span>
                          </div>
                          <div className="text-xs font-mono text-gray-500">D+{parent.relative_start_day_offset} → D+{parent.relative_due_day_offset}</div>
                        </div>
                        {parent.children.map((child) => {
                          const ChildIcon = TYPE_ICON[child.item_type] || ListTree;
                          return (
                            <div key={child.template_item_id} className="pl-12 pr-5 py-2.5 flex items-center justify-between text-sm" data-testid={`wbs-item-${child.template_item_id}`}>
                              <div className="flex items-center gap-2">
                                <ChildIcon className={`w-3.5 h-3.5 ${child.item_type === "milestone" ? "text-red-500" : "text-gray-400"}`} strokeWidth={1.7} />
                                <span className={child.item_type === "milestone" ? "font-semibold" : ""}>{child.title}</span>
                              </div>
                              <div className="text-xs font-mono text-gray-500">D+{child.relative_start_day_offset} → D+{child.relative_due_day_offset}</div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      <Dialog open={openInstantiate} onOpenChange={setOpenInstantiate}>
        <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-white">Create project from template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Linked deal (CRM) — optional</Label>
              <select
                value={form.deal_id}
                onChange={(e) => setForm({ ...form, deal_id: e.target.value })}
                className="w-full rounded-none mt-1 h-10 border border-[#374151] bg-[#0D1117] text-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#00F5D4]"
                data-testid="instantiate-deal-select"
              >
                <option value="">No deal — name from template</option>
                {deals.map((d) => (
                  <option key={d.deal_id} value={d.deal_id}>{d.name}{d.client ? ` (${d.client})` : ""}</option>
                ))}
              </select>
              <div className="text-[11px] text-gray-500 mt-1">
                If linked, project is auto-named <span className="font-mono">"&lt;Deal&gt; – Execution"</span>.
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Project name override (optional)</Label>
              <Input className="rounded-none mt-1" placeholder="Leave blank to auto-name" data-testid="instantiate-name-input" value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Start date</Label>
              <Input type="date" className="rounded-none mt-1" data-testid="instantiate-date-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              <div className="text-[11px] text-gray-500 mt-1">
                <Calendar className="w-3 h-3 inline mr-1" />
                All {selected?.items?.length || 0} items will be scheduled relative to this date.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={instantiate} disabled={busy} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="confirm-instantiate-btn">
              {busy ? "Creating…" : <>Generate project <ArrowUpRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish dialog */}
      <Dialog open={openPublish} onOpenChange={setOpenPublish}>
        <DialogContent className="rounded-none bg-[#111827] border-[#374151] text-white sm:max-w-md" data-testid="publish-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Publish to marketplace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-400">
              List <strong className="font-display">{selected?.name}</strong> on the public marketplace.
              Set a price (or 0 for free).
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Price</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className="rounded-none mt-1 font-mono"
                  value={publishForm.price}
                  onChange={(e) => setPublishForm({ ...publishForm, price: e.target.value })}
                  data-testid="publish-price-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Currency</Label>
                <select
                  value={publishForm.currency}
                  onChange={(e) => setPublishForm({ ...publishForm, currency: e.target.value })}
                  className="w-full rounded-none mt-1 h-10 border border-[#374151] bg-[#0D1117] text-white px-3 text-sm"
                  data-testid="publish-currency-select"
                >
                  <option>GBP</option>
                  <option>USD</option>
                  <option>EUR</option>
                </select>
              </div>
            </div>
            <div className="text-[11px] text-gray-500 bg-[#030712] border border-[#1F2937] px-3 py-2">
              Other workspaces will be able to purchase and clone this template into their own library. You can unpublish at any time — buyers keep their copies.
            </div>
          </div>
          <DialogFooter>
            <Button onClick={publish} disabled={busy} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="confirm-publish-btn">
              {busy ? "Publishing…" : (Number(publishForm.price) > 0 ? `Publish for ${publishForm.currency} ${publishForm.price}` : "Publish as free")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Templates;
